import { useState, useEffect, useMemo, useCallback } from 'react'
import { UserCheck, Users, School, Loader2, Search, Plus, X, Check, ArrowRightLeft, GraduationCap, Filter, AlertTriangle } from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'

// CORRECT localStorage keys for your app
const SCHOOL_ID_KEY = 'lybibal-selected-school-id'
const YEAR_ID_KEY = 'lybibal-selected-year-id'
const YEAR_LABEL_KEY = 'lybibal-selected-year-label'

interface Student {
  id: number
  matricule: string
  first_name: string
  last_name: string
  gender: 'M' | 'F'
  date_of_birth?: string
  previous_school?: string
  status: 'active' | 'inactive' | 'transferred' | 'excluded'
  school_id: number
  academic_year_id: number
}

interface ClassItem {
  id: number
  name: string
  abbreviation: string
  level_id: number
  level_name: string
  cycle_name: string
  section_name: string
  specialty_name?: string
  capacity: number
  current_enrollment: number
  remaining_places: number
  school_id: number
  academic_year_id: number
}

interface Enrollment {
  id: number
  student_id: number
  student_name: string
  student_matricule: string
  class_id: number
  class_name: string
  enrollment_date: string
  status: 'active' | 'transferred' | 'excluded'
  school_id: number
  academic_year_id: number
}

function cn(...classes: (string | false | null | undefined)[]) { return classes.filter(Boolean).join(' ') }

export default function AcademicAssignPage() {
  const { lang } = useLang()
  const [loading, setLoading] = useState(true)
  const [students, setStudents] = useState<Student[]>([])
  const [classes, setClasses] = useState<ClassItem[]>([])
  const [enrollments, setEnrollments] = useState<Enrollment[]>([])
  const [activeTab, setActiveTab] = useState<'assign' | 'enrolled' | 'unassigned'>('assign')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedClass, setSelectedClass] = useState<number | ''>('')
  const [selectedStudents, setSelectedStudents] = useState<number[]>([])
  const [isAssigning, setIsAssigning] = useState(false)

  // Get school_id and academic_year_id from localStorage using CORRECT keys
  const selectedSchoolId = useMemo(() => {
    const stored = localStorage.getItem(SCHOOL_ID_KEY)
    if (stored) {
      const val = Number(stored)
      if (!isNaN(val) && val > 0) return val
    }
    // Fallback: try auth-storage (Zustand format)
    const auth = localStorage.getItem('auth-storage')
    if (auth) {
      try {
        const parsed = JSON.parse(auth)
        const sid = parsed?.state?.user?.school_id || parsed?.state?.school_id
        if (sid) return Number(sid)
      } catch { /* ignore */ }
    }
    return null
  }, [])

  const selectedAcademicYearId = useMemo(() => {
    const stored = localStorage.getItem(YEAR_ID_KEY)
    if (stored) {
      const val = Number(stored)
      if (!isNaN(val) && val > 0) return val
    }
    // Fallback: try lybibal-academic-year
    const ay = localStorage.getItem('lybibal-academic-year')
    if (ay) {
      const val = Number(ay)
      if (!isNaN(val) && val > 0) return val
    }
    // Fallback: try auth-storage
    const auth = localStorage.getItem('auth-storage')
    if (auth) {
      try {
        const parsed = JSON.parse(auth)
        const yid = parsed?.state?.user?.academic_year_id || parsed?.state?.academic_year_id
        if (yid) return Number(yid)
      } catch { /* ignore */ }
    }
    return null
  }, [])

  const selectedYearLabel = useMemo(() => {
    return localStorage.getItem(YEAR_LABEL_KEY) || ''
  }, [])

  // Listen for academic year changes from other components
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SCHOOL_ID_KEY || e.key === YEAR_ID_KEY || e.key === 'lybibal-academic-year') {
        window.location.reload()
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  // Load data filtered by school and year
  const loadData = useCallback(async () => {
    if (!selectedSchoolId || !selectedAcademicYearId) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      // TODO: Replace with actual API calls when backend endpoints are ready
      // const studentsData = await studentsApi.getUnassigned(selectedSchoolId, selectedAcademicYearId)
      // const classesData = await classesApi.getAll(selectedSchoolId, selectedAcademicYearId)
      // const enrollmentsData = await enrollmentApi.getAll(selectedSchoolId, selectedAcademicYearId)
      // setStudents(studentsData || [])
      // setClasses(classesData || [])
      // setEnrollments(enrollmentsData || [])
      
      // For now, empty state until backend is ready
      setStudents([])
      setClasses([])
      setEnrollments([])
    } catch (err) {
      console.error('Error loading assignment data:', err)
      toast.error(lang === 'fr' ? 'Erreur chargement' : 'Loading error')
    } finally {
      setLoading(false)
    }
  }, [selectedSchoolId, selectedAcademicYearId, lang])

  useEffect(() => {
    loadData()
  }, [loadData])

  const filteredClasses = useMemo(() => {
    if (!searchTerm) return classes
    return classes.filter(c => 
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.level_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.abbreviation.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [classes, searchTerm])

  const filteredStudents = useMemo(() => {
    if (!searchTerm) return students
    return students.filter(s => 
      s.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.matricule.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [students, searchTerm])

  const filteredEnrollments = useMemo(() => {
    if (!searchTerm) return enrollments
    return enrollments.filter(e => 
      e.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.student_matricule.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.class_name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [enrollments, searchTerm])

  const unassignedStudents = useMemo(() => {
    const enrolledIds = new Set(enrollments.map(e => e.student_id))
    return students.filter(s => !enrolledIds.has(s.id))
  }, [students, enrollments])

  const stats = useMemo(() => ({
    totalStudents: students.length,
    enrolled: enrollments.filter(e => e.status === 'active').length,
    unassigned: unassignedStudents.length,
    transferred: enrollments.filter(e => e.status === 'transferred').length,
    excluded: enrollments.filter(e => e.status === 'excluded').length,
    fullClasses: classes.filter(c => c.remaining_places === 0).length,
    availablePlaces: classes.reduce((sum, c) => sum + c.remaining_places, 0),
  }), [students, enrollments, unassignedStudents, classes])

  const handleToggleStudent = (studentId: number) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    )
  }

  const handleAssignStudents = async () => {
    if (!selectedClass || selectedStudents.length === 0) {
      toast.error(lang === 'fr' ? 'Veuillez sélectionner une classe et des élèves' : 'Please select a class and students')
      return
    }
    if (!selectedSchoolId || !selectedAcademicYearId) return

    setIsAssigning(true)
    try {
      // TODO: Replace with actual API call
      // await enrollmentApi.createBulk({
      //   class_id: Number(selectedClass),
      //   student_ids: selectedStudents,
      //   school_id: selectedSchoolId,
      //   academic_year_id: selectedAcademicYearId
      // })
      
      toast.success(lang === 'fr' 
        ? `${selectedStudents.length} élève(s) affecté(s)` 
        : `${selectedStudents.length} student(s) assigned`)
      setSelectedStudents([])
      setSelectedClass('')
      await loadData()
    } catch (err) {
      console.error('Error assigning students:', err)
      toast.error(lang === 'fr' ? "Erreur lors de l'affectation" : 'Error during assignment')
    } finally {
      setIsAssigning(false)
    }
  }

  const handleTransferStudent = async (enrollmentId: number, newClassId: number) => {
    if (!selectedSchoolId || !selectedAcademicYearId) return
    try {
      // TODO: Replace with actual API call
      // await enrollmentApi.transfer(enrollmentId, {
      //   new_class_id: newClassId,
      //   school_id: selectedSchoolId,
      //   academic_year_id: selectedAcademicYearId
      // })
      
      toast.success(lang === 'fr' ? 'Transfert effectué' : 'Transfer completed')
      await loadData()
    } catch (err) {
      toast.error(lang === 'fr' ? 'Erreur de transfert' : 'Transfer error')
    }
  }

  const handleExcludeStudent = async (enrollmentId: number) => {
    if (!confirm(lang === 'fr' ? 'Confirmer l\'exclusion ?' : 'Confirm exclusion?')) return
    if (!selectedSchoolId || !selectedAcademicYearId) return
    try {
      // TODO: Replace with actual API call
      // await enrollmentApi.exclude(enrollmentId, {
      //   school_id: selectedSchoolId,
      //   academic_year_id: selectedAcademicYearId
      // })
      
      toast.success(lang === 'fr' ? 'Exclusion effectuée' : 'Exclusion completed')
      await loadData()
    } catch (err) {
      toast.error(lang === 'fr' ? 'Erreur d\'exclusion' : 'Exclusion error')
    }
  }

  // Show warning if no school/year selected
  if (!selectedSchoolId || !selectedAcademicYearId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <School className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-600">
            {lang === 'fr' ? 'Aucun établissement ou année scolaire sélectionné' : 'No school or academic year selected'}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            {lang === 'fr' ? 'Veuillez sélectionner une année scolaire dans le header' : 'Please select an academic year in the header'}
          </p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-12 h-12 text-green-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50/20 to-blue-50/20">
      <div className="page-header bg-white/80 backdrop-blur-md border-b border-green-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-green-700 rounded-xl flex items-center justify-center shadow-lg shadow-green-200">
              <UserCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="page-title text-2xl font-bold bg-gradient-to-r from-green-700 to-green-900 bg-clip-text text-transparent">
                {lang === 'fr' ? "Mettre l'élève en classe" : 'Assign Student to Class'}
              </h1>
              <p className="page-subtitle text-sm text-gray-500 mt-0.5">
                {lang === 'fr' 
                  ? `${stats.totalStudents} élèves \u2014 ${stats.enrolled} affectés / ${stats.unassigned} non affectés` 
                  : `${stats.totalStudents} students \u2014 ${stats.enrolled} assigned / ${stats.unassigned} unassigned`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Badge année scolaire */}
            {selectedYearLabel && (
              <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                <School className="w-3 h-3 mr-1.5" />
                {selectedYearLabel}
              </span>
            )}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button 
                onClick={() => setActiveTab('assign')} 
                className={cn('px-4 py-2 rounded-md text-sm font-medium transition-all', activeTab === 'assign' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-600 hover:text-gray-800')}
              >
                {lang === 'fr' ? 'Affectation' : 'Assignment'}
              </button>
              <button 
                onClick={() => setActiveTab('enrolled')} 
                className={cn('px-4 py-2 rounded-md text-sm font-medium transition-all', activeTab === 'enrolled' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-600 hover:text-gray-800')}
              >
                {lang === 'fr' ? 'Affectés' : 'Enrolled'}
              </button>
              <button 
                onClick={() => setActiveTab('unassigned')} 
                className={cn('px-4 py-2 rounded-md text-sm font-medium transition-all', activeTab === 'unassigned' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-600 hover:text-gray-800')}
              >
                {lang === 'fr' ? 'Non affectés' : 'Unassigned'}
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input 
                type="text" 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                placeholder={lang === 'fr' ? 'Rechercher...' : 'Search...'} 
                className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400 w-64" 
              />
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-green-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.totalStudents}</p>
                <p className="text-xs text-gray-500">{lang === 'fr' ? 'Total élèves' : 'Total students'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Check className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.enrolled}</p>
                <p className="text-xs text-gray-500">{lang === 'fr' ? 'Affectés' : 'Enrolled'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-amber-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats.unassigned}</p>
                <p className="text-xs text-gray-500">{lang === 'fr' ? 'Non affectés' : 'Unassigned'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{stats.availablePlaces}</p>
                <p className="text-xs text-gray-500">{lang === 'fr' ? 'Places dispo.' : 'Avail. places'}</p>
              </div>
            </div>
          </div>
        </div>

        {activeTab === 'assign' && (
          <>
            {/* Class selection */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 bg-gradient-to-r from-green-50 to-green-100/50 border-b border-green-100">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-green-600" />
                  {lang === 'fr' ? 'Sélectionner une classe' : 'Select a class'}
                </h2>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredClasses.map((cls) => (
                    <button
                      key={cls.id}
                      onClick={() => setSelectedClass(cls.id)}
                      className={cn(
                        'p-4 rounded-xl border-2 text-left transition-all',
                        selectedClass === cls.id 
                          ? 'border-green-500 bg-green-50 shadow-md' 
                          : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                      )}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-gray-900">{cls.name}</span>
                        <span className={cn(
                          'text-xs px-2 py-1 rounded-full font-medium',
                          cls.remaining_places > 5 ? 'bg-emerald-100 text-emerald-700' :
                          cls.remaining_places > 0 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        )}>
                          {cls.remaining_places} {lang === 'fr' ? 'places' : 'places'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{cls.level_name} \u2014 {cls.cycle_name}</p>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${(cls.current_enrollment / cls.capacity) * 100}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {cls.current_enrollment}/{cls.capacity} {lang === 'fr' ? 'élèves' : 'students'}
                      </p>
                    </button>
                  ))}
                </div>
                {filteredClasses.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{lang === 'fr' ? 'Aucune classe disponible' : 'No classes available'}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Student selection and assignment */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  {lang === 'fr' ? 'Élèves non affectés' : 'Unassigned students'}
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {selectedStudents.length} {lang === 'fr' ? 'sélectionné(s)' : 'selected'}
                  </span>
                  <button
                    onClick={handleAssignStudents}
                    disabled={!selectedClass || selectedStudents.length === 0 || isAssigning}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                      selectedClass && selectedStudents.length > 0 && !isAssigning
                        ? 'bg-green-600 text-white hover:bg-green-700 shadow-md shadow-green-200'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    )}
                  >
                    {isAssigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    {lang === 'fr' ? 'Affecter' : 'Assign'}
                  </button>
                </div>
              </div>
              
              {unassignedStudents.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-100">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-12">
                          <input 
                            type="checkbox" 
                            checked={selectedStudents.length === unassignedStudents.length && unassignedStudents.length > 0}
                            onChange={() => {
                              if (selectedStudents.length === unassignedStudents.length) {
                                setSelectedStudents([])
                              } else {
                                setSelectedStudents(unassignedStudents.map(s => s.id))
                              }
                            }}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Matricule' : 'Matricule'}</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Nom' : 'Name'}</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Prénom' : 'First name'}</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Sexe' : 'Gender'}</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'École précédente' : 'Previous school'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredStudents.map((student) => (
                        <tr 
                          key={student.id} 
                          className={cn(
                            'hover:bg-gray-50/50 transition-colors cursor-pointer',
                            selectedStudents.includes(student.id) && 'bg-green-50/50'
                          )}
                          onClick={() => handleToggleStudent(student.id)}
                        >
                          <td className="py-3 px-4">
                            <input 
                              type="checkbox" 
                              checked={selectedStudents.includes(student.id)}
                              onChange={() => handleToggleStudent(student.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                          </td>
                          <td className="py-3 px-4 text-sm font-medium text-gray-900">{student.matricule}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">{student.last_name}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">{student.first_name}</td>
                          <td className="py-3 px-4">
                            <span className={cn(
                              'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                              student.gender === 'M' ? 'bg-blue-50 text-blue-700' : 'bg-pink-50 text-pink-700'
                            )}>
                              {student.gender === 'M' ? (lang === 'fr' ? 'Garçon' : 'Boy') : (lang === 'fr' ? 'Fille' : 'Girl')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">{student.previous_school || '\u2014'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg font-medium">{lang === 'fr' ? 'Tous les élèves sont affectés' : 'All students are assigned'}</p>
                  <p className="text-sm mt-1">{lang === 'fr' ? 'Aucun élève en attente d\'affectation' : 'No students waiting for assignment'}</p>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'enrolled' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                {lang === 'fr' ? 'Élèves affectés' : 'Enrolled students'}
              </h2>
              <span className="text-sm text-gray-500">{filteredEnrollments.length} {lang === 'fr' ? 'résultat(s)' : 'result(s)'}</span>
            </div>
            
            {filteredEnrollments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Matricule' : 'Matricule'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Élève' : 'Student'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Classe' : 'Class'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Date d\'affectation' : 'Enrollment date'}</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Statut' : 'Status'}</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Actions' : 'Actions'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredEnrollments.map((enrollment) => (
                      <tr key={enrollment.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{enrollment.student_matricule}</td>
                        <td className="py-3 px-4 text-sm text-gray-700">{enrollment.student_name}</td>
                        <td className="py-3 px-4 text-sm text-gray-700">{enrollment.class_name}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{new Date(enrollment.enrollment_date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={cn(
                            'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border',
                            enrollment.status === 'active' && 'bg-emerald-50 text-emerald-700 border-emerald-200',
                            enrollment.status === 'transferred' && 'bg-blue-50 text-blue-700 border-blue-200',
                            enrollment.status === 'excluded' && 'bg-red-50 text-red-700 border-red-200'
                          )}>
                            {enrollment.status === 'active' && (lang === 'fr' ? 'Actif' : 'Active')}
                            {enrollment.status === 'transferred' && (lang === 'fr' ? 'Transféré' : 'Transferred')}
                            {enrollment.status === 'excluded' && (lang === 'fr' ? 'Exclu' : 'Excluded')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button 
                              onClick={() => handleTransferStudent(enrollment.id, 0)} 
                              className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors" 
                              title={lang === 'fr' ? 'Transférer' : 'Transfer'}
                            >
                              <ArrowRightLeft className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleExcludeStudent(enrollment.id)} 
                              className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" 
                              title={lang === 'fr' ? 'Exclure' : 'Exclude'}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg font-medium">{lang === 'fr' ? 'Aucun élève affecté' : 'No enrolled students'}</p>
                <p className="text-sm mt-1">{lang === 'fr' ? 'Utilisez l\'onglet Affectation pour assigner des élèves' : 'Use the Assignment tab to assign students'}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'unassigned' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                {lang === 'fr' ? 'Élèves non affectés' : 'Unassigned students'}
              </h2>
              <span className="text-sm text-gray-500">{unassignedStudents.length} {lang === 'fr' ? 'résultat(s)' : 'result(s)'}</span>
            </div>
            
            {unassignedStudents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Matricule' : 'Matricule'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Nom' : 'Name'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Prénom' : 'First name'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Sexe' : 'Gender'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'École précédente' : 'Previous school'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {unassignedStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{student.matricule}</td>
                        <td className="py-3 px-4 text-sm text-gray-700">{student.last_name}</td>
                        <td className="py-3 px-4 text-sm text-gray-700">{student.first_name}</td>
                        <td className="py-3 px-4">
                          <span className={cn(
                            'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                            student.gender === 'M' ? 'bg-blue-50 text-blue-700' : 'bg-pink-50 text-pink-700'
                          )}>
                            {student.gender === 'M' ? (lang === 'fr' ? 'Garçon' : 'Boy') : (lang === 'fr' ? 'Fille' : 'Girl')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">{student.previous_school || '\u2014'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Check className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg font-medium">{lang === 'fr' ? 'Tous les élèves sont affectés' : 'All students are assigned'}</p>
                <p className="text-sm mt-1">{lang === 'fr' ? 'Aucun élève en attente d\'affectation pour cette année' : 'No students waiting for assignment this year'}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
